"use client";

import { cn } from "@plura/shared/utils";
import Link from "next/link";
import { useState } from "react";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";

interface Item {
  title: string;
  description: string;
}

interface Props {
  title: string;
  badge?: string;
  caption?: string;
  items: Item[];
}

const Faqs = ({ title, badge, caption, items }: Props) => {
  // Single-open accordion; first item open to match the design.
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  // FAQPage structured data — preserves the rich-result eligibility the legacy
  // Faqs component emitted (Google FAQ rich snippets).
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.title,
      acceptedAnswer: { "@type": "Answer", text: item.description },
    })),
  };

  return (
    <section className="section-spacing">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <div className="landing-container grid items-start gap-12 lg:grid-cols-[2fr_3fr] lg:gap-20">
        {/* Left — badge, title, caption, button, help */}
        <div className="flex flex-col items-start gap-10">
          <div className="flex flex-col items-start gap-6">
            {badge && (
              <Badge variant="pill" className="uppercase tracking-[0.12em]">
                {badge}
              </Badge>
            )}
            <h2 className="text-h2-medium font-heading text-dark">{title}</h2>
            {caption && <p className="text-description">{caption}</p>}
          </div>

          <div className="flex flex-col items-start gap-7">
            <Button variant="secondary" size="medium" href="https://app.formsuite.co/sign-up">
              Create a free form
            </Button>
            <p className="text-dark/60 font-body text-sm">
              Still need help?{" "}
              <Link href="/contact" className="text-dark underline">
                Contact Us
              </Link>
            </p>
          </div>
        </div>

        {/* Right — accordion */}
        <div className="flex flex-col gap-0.5">
          {items.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={item.title}
                className={cn(
                  "rounded-2xl border transition-[box-shadow,border-color] duration-300",
                  isOpen
                    ? "border-transparent bg-white shadow-[0px_3px_5px_rgba(29,29,31,0.06),10px_16px_52px_rgba(29,29,31,0.1)]"
                    : "border-transparent hover:border-[#d9d9d9]",
                )}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="flex w-full cursor-pointer items-center gap-5 p-6 text-left"
                >
                  <Chevron
                    className={cn(
                      "size-6 shrink-0 text-dark transition-transform duration-300",
                      isOpen && "rotate-180",
                    )}
                  />
                  <span className="font-body text-lg font-medium text-dark">{item.title}</span>
                </button>

                {/* Animated answer (grid 0fr → 1fr) */}
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-300 ease-out",
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="font-body pr-6 pb-6 pl-[68px] text-[16px] leading-[26px] text-[#363636]">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

function Chevron({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className={className}>
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default Faqs;
